import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useConfirm } from '@hooks/useConfirm';
import type { UseFormReturn } from 'react-hook-form';
import type { UnitFormValues } from '@/features/verification/schemas/unit.schema';

interface UnitDialogProps {
  form: UseFormReturn<UnitFormValues>;
  open: boolean;
  isEditing: boolean;
  isDirty: boolean;
  onSubmit: () => void;
  onClose: () => void;
}

const UnitDialog: React.FC<UnitDialogProps> = ({
  form,
  open,
  isEditing,
  isDirty,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const unitNameRef = React.useRef<HTMLInputElement>(null);

  const dismiss = async () => {
    if (
      isDirty &&
      !(await confirm({
        title: t('confirmation.discardChangesTitle'),
        description: t('confirmation.unsavedChanges'),
        confirmLabel: t('confirmation.discardChangesConfirm'),
        destructive: false,
      }))
    ) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) void dismiss();
      }}
    >
      {/* The inset keeps the MUI dialog's viewport gutter, which `w-full` alone drops. */}
      <DialogContent
        className="revy-ui w-[calc(100%-2rem)] max-w-[600px] sm:max-w-[600px]"
        aria-describedby={undefined}
        // Radix otherwise focuses the first tabbable field; the autoFocus this
        // replaces sat on the second one.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          unitNameRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle className="revy-cjk text-lg leading-[1.5]">
            {isEditing ? t('verification.editUnit') : t('verification.addUnit')}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void onSubmit();
            }}
          >
            <div className="flex flex-col gap-4 pt-2">
              <FormField
                control={form.control}
                name="unitNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="revy-cjk">{t('verification.unitNumber')}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="04" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="unitName"
                render={({ field }) => (
                  <FormItem required>
                    <FormLabel className="revy-cjk">
                      {t('verification.unitNameLabel')}
                    </FormLabel>
                    <FormControl>
                      {/*
                        Compose both refs. `ref={unitNameRef}` alone would land
                        AFTER `{...field}` and drop `field.ref`, leaving the
                        input unregistered with RHF — `shouldFocusError` and
                        `setFocus('unitName')` would then no-op silently.
                      */}
                      <Input
                        {...field}
                        ref={(el) => {
                          field.ref(el);
                          unitNameRef.current = el;
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="gap-2 pt-4 sm:gap-0">
              <Button type="button" variant="outline" onClick={onClose}>
                <span className="revy-cjk">{t('common.cancel')}</span>
              </Button>
              {/*
                Gated on the unit name exactly as before the RHF migration, not
                on formState.isValid: `isValid` is only accurate under
                mode:'onChange', and switching modes would make errors appear
                while typing — a behaviour change this migration must not make.
              */}
              <Button
                type="submit"
                disabled={!form.watch('unitName').trim() || form.formState.isSubmitting}
              >
                <span className="revy-cjk">{t('common.save')}</span>
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UnitDialog;
